import React, { useMemo, useState, useEffect } from 'react'
import { makeStyles, Typography, Container } from '@material-ui/core'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import useQuery from '../../../hooks/useQuery'
import TrainingClassDetailsCard from '../../../components/Training/TrainingClassDetailsCard'
import StyledChip from '../../../components/Common/StyledChip'
import { standardizeEndOfDateUTC, standardizeStartOfDateUTC, getClassStatus } from '../../../utils'
import TrainingEnrollmentAttendanceCard from '../../../components/Training/TrainingEnrollmentAttendanceCard'
import TrainingInvoiceCard from '../../../components/Training/TrainingInvoiceCard'

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1,
		paddingTop: '24px',
		paddingLeft: '20px !important',
		paddingRight: '20px !important'
	},
	topContainer: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
	headerContainer: { display: 'flex', alignItems: 'center', gap: '1rem' },
	header: { fontWeight: 800, fontSize: '1.375rem' },
	statusChip: {
		marginLeft: 16,
		'& .MuiChip-label': {
			marginRight: 0
		}
	}
}))
const TrainingEnrollment = (props) => {
	const classes = useStyles()
	const { params } = useQuery()

	const [classDetails, setClassDetails] = useState(null)
	const [enrollmentDetails, setEnrollmentDetails] = useState({})
	const [loadingEnrollment, setLoadingEnrollment] = useState(false)
	const [loadingClassDetails, setLoadingClassDetails] = useState(false)

	const { facility, trainingClasses, getTrainingClassForFacility, getEnrollmentForClass } = useSelectedFacility()

	useEffect(() => {
		const classId = params.classId

		if (!trainingClasses || trainingClasses.length === 0 || !classId) {
			return
		}

		const classDetails = trainingClasses[classId] || null
		setClassDetails(classDetails)
	}, [trainingClasses, setClassDetails])

	useEffect(() => {
		const get = async () => {
			if (!params.classId || !params.facilityId || !params.enrollmentId) {
				return
			}
			setLoadingClassDetails(true)
			await getTrainingClassForFacility({
				class_id: params.classId,
				facility_id: params.facilityId
			})
			setLoadingClassDetails(false)
		}
		if (!classDetails) {
			get()
		}
	}, [getTrainingClassForFacility, classDetails])

	useEffect(() => {
		const get = async () => {
			if (!params.facilityId || !params.enrollmentId) {
				return
			}
			setLoadingEnrollment(true)
			const enrollment = await getEnrollmentForClass({
				facility_id: params.facilityId,
				enrollment_id: params.enrollmentId
			})
			setLoadingEnrollment(false)

			if (enrollment) {
				const { data, errors, success } = enrollment
				if (success) {
					setEnrollmentDetails(data[0])
				}
			}
		}
		if (Object.keys(enrollmentDetails).length === 0) {
			get()
		}
	}, [getEnrollmentForClass, enrollmentDetails, setLoadingEnrollment])

	const classCancelled = useMemo(() => {
		if (!classDetails) {
			return false
		}
		return classDetails.cancelled
	}, [classDetails])

	const classHeader = useMemo(() => {
		if (!classDetails) {
			return 'Class Details'
		}

		const { dates } = classDetails
		const start = standardizeStartOfDateUTC(dates[0])
		const end = standardizeEndOfDateUTC(dates[dates.length - 1])

		const startDay = start.format('MMM DD')
		let endDay = end.format('MMM DD')
		if (start.get('month') === end.get('month')) {
			endDay = end.format('DD')
		}
		return `${startDay}-${endDay} Enrollment`
	}, [classDetails])

	const classStatus = useMemo(() => {
		if (!classDetails) {
			return null
		}

		return getClassStatus(classDetails, facility)
	}, [classDetails, facility])

	const classLabel = useMemo(() => {
		if (!classStatus) {
			return { name: 'Unknown', type: 'red' }
		}
		if (classCancelled) {
			return { name: 'Cancelled', type: 'red' }
		}
		if (classStatus.isBetween) {
			return { name: 'In Progress', type: 'green' }
		}
		if (classStatus.isBefore) {
			return { name: 'Upcoming', type: 'purple' }
		}
		if (classStatus.isAfter) {
			return { name: 'Completed', type: 'gray' }
		}
	}, [classStatus, classCancelled])

	return (
		<>
			<Container className={classes.root} maxWidth="md">
				<div>
					<div className={classes.topContainer}>
						<div className={classes.headerContainer}>
							<Typography className={classes.header}>{classHeader}</Typography>
							<StyledChip
								label={classLabel.name}
								skiptownvariant={classLabel.type}
								style={{ textTransform: 'uppercase', fontWeight: 800 }}
							/>
						</div>
					</div>
					<TrainingClassDetailsCard loading={loadingClassDetails} trainingClass={classDetails} />
					<TrainingEnrollmentAttendanceCard loading={loadingEnrollment} enrollment={enrollmentDetails} />
					<TrainingInvoiceCard
						line_items={enrollmentDetails?.line_items || []}
						loading={loadingEnrollment}
					/>
				</div>
			</Container>
		</>
	)
}

export default TrainingEnrollment
